import { Component, OnInit, ViewEncapsulation } from '@angular/core';

import { take } from 'rxjs/operators';

import {
  AddressService,
  CardSetFooterConfiguration,
  CardSetHeaderConfiguration,
  CardSetMenuConfiguration,
  CardSetTransformationService,
  CoreService,
  DialogService,
  FileService,
  FormConfigurationRequest,
  FormConfigurationService,
  HeaderService,
  MenuConfigService,
  MenuService,
  UserService
} from 'dspx-core';
import { CardSetFooterLink } from 'dspx-core/lib/interfaces/card-set-footer-link.interface';

import { API_BASE_URLS_NOT_REQUIRING_SPINNER, API_ENDPOINT_KEYS, API_ENDPOINTS } from './constants/api-endpoints.constants';
import { APP_DATA_KEYS, APPLICATION, POP_UP_CODES, POP_UP_TEXTS } from "./constants/app-data.constants";
import { TEMPLATES, VERSIONS } from './constants/templates.constants';
import { ROUTE_CONSTANTS } from './constants/route.constants';

import { AppDataService } from './services/app-data.service';
import { HeaderCustomisationService } from './services/header-customisation.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit {

  constructor(
    private appDataService: AppDataService,
    private headerCustomisationService: HeaderCustomisationService,
    private coreService: CoreService,
    private addressService: AddressService,
    private fileService: FileService,
    private userService: UserService,
    private formConfigurationService: FormConfigurationService,
    private menuService: MenuService,
    private headerService: HeaderService,
    private cardSetTransformationService: CardSetTransformationService,
    private dialogService: DialogService,
    protected menuConfigService: MenuConfigService) {
  }

  environmentBannerMessage: string;
  environmentBannerBgColor: string;
  environmentBannerColor: string;
  headerConfig: CardSetHeaderConfiguration;
  footerConfig: CardSetFooterConfiguration;
  menuConfig: CardSetMenuConfiguration;

  public headerLinks: any = {};
  public isMenuVisible: boolean = false;  //Set this to false if you have an app that starts on an unauthenticated component.
  public showLogout: boolean = false;     //Set this to false if you have an app that starts on an unauthenticated component.
  //app-data.service.ts has a function called 'getUserDetails' (that you can modify to your liking) which you can call from
  //an authenticated component to set the header config with the user details and display a menu with the logout option.

  ngOnInit() {

    this.menuService.menuLoaded$.subscribe((config) => {
      if (config) {
        this.menuConfig = config;
      }
    });

    this.headerService.headerUpdated$.subscribe((config) => {
      if (config) {
        this.headerConfig = config;
      }
    });

    this.menuConfigService.styleConfigService.layoutMode =
      'static'; /*'overlay';*/ /*'horizontal';*/ /*'slim';*/
    this.menuConfigService.styleConfigService.layoutColor = /*'dark';*/ 'light';
    this.menuConfigService.styleConfigService.darkMenu = /*true;*/ false;
    this.menuConfigService.styleConfigService.ripple = true; /*false;*/
    this.menuConfigService.styleConfigService.inputStyle = 'outlined'; /*'filled';*/

    this.menuConfigService.primengConfig.ripple =
      this.menuConfigService.styleConfigService.ripple;

    this.userService.endpoints = {
      getLoggedInUser: API_ENDPOINTS.user.getLoggedInUser,
    };

    this.formConfigurationService.endpoints = {
      [API_ENDPOINT_KEYS.getFormConfig]: API_ENDPOINTS.configuration.default,
      [API_ENDPOINT_KEYS.getIntegrityOfficerConfigAndResponse]: API_ENDPOINTS.integrityOfficer.getConfigAndResponse,
      [API_ENDPOINT_KEYS.getReferenceDataConfigAndResponse]: API_ENDPOINTS.referenceData.getConfigAndResponse,
      [API_ENDPOINT_KEYS.getCoiFormConfigAndResponse]: API_ENDPOINTS.coiForm.getConfigAndResponse,
      [API_ENDPOINT_KEYS.getCoiFormConfigAndResponseReadOnly]: API_ENDPOINTS.coiForm.getConfigAndResponseReadOnly,
      [API_ENDPOINT_KEYS.getCoiFormConfigAndResponseExternal]: API_ENDPOINTS.coiForm.getConfigAndResponseExternal,
      [API_ENDPOINT_KEYS.getCoiFormConfigAndResponseExternalReadOnly]: API_ENDPOINTS.coiForm.getConfigAndResponseExternalReadOnly,
      [API_ENDPOINT_KEYS.getCoiFormConfigWithEmptyResponse]: API_ENDPOINTS.coiForm.getConfigWithEmptyResponse,
      [API_ENDPOINT_KEYS.getApproverCoiFormConfigAndResponse]: API_ENDPOINTS.approverCoiForm.getConfigAndResponse,
      [API_ENDPOINT_KEYS.getApproverCoiFormConfigAndResponseReadOnly]: API_ENDPOINTS.approverCoiForm.getConfigAndResponseReadOnly,
      [API_ENDPOINT_KEYS.getDopiFormConfigAndResponse]: API_ENDPOINTS.dopiForm.getConfigAndResponse,
      [API_ENDPOINT_KEYS.getDopiFormConfigAndResponseReadOnly]: API_ENDPOINTS.dopiForm.getConfigAndResponseReadOnly,
      [API_ENDPOINT_KEYS.getDopiFormConfigAndResponseExternal]: API_ENDPOINTS.dopiForm.getConfigAndResponseExternal,
      [API_ENDPOINT_KEYS.getDopiFormConfigAndResponseExternalReadOnly]: API_ENDPOINTS.dopiForm.getConfigAndResponseExternalReadOnly,
      [API_ENDPOINT_KEYS.getDopiFormConfigWithEmptyResponse]: API_ENDPOINTS.dopiForm.getConfigWithEmptyResponse,
      [API_ENDPOINT_KEYS.getApproverDopiFormConfigAndResponse]: API_ENDPOINTS.approverDopiForm.getConfigAndResponse,
      [API_ENDPOINT_KEYS.getApproverDopiFormConfigAndResponseReadOnly]: API_ENDPOINTS.approverDopiForm.getConfigAndResponseReadOnly,
      [API_ENDPOINT_KEYS.getGbhFormConfigAndResponse]: API_ENDPOINTS.gbhForm.getConfigAndResponse,
      [API_ENDPOINT_KEYS.getGbhFormConfigAndResponseReadOnly]: API_ENDPOINTS.gbhForm.getConfigAndResponseReadOnly,
      [API_ENDPOINT_KEYS.getGbhFormConfigWithEmptyResponse]: API_ENDPOINTS.gbhForm.getConfigWithEmptyResponse,
      [API_ENDPOINT_KEYS.getApproverGbhFormConfigAndResponse]: API_ENDPOINTS.approverGbhForm.getConfigAndResponse,
      [API_ENDPOINT_KEYS.getApproverGbhFormConfigAndResponseReadOnly]: API_ENDPOINTS.approverGbhForm.getConfigAndResponseReadOnly
    }

    this.addressService.endpoints = {
      //Note: Do not change the 'addressApiSearchEndpoint' and 'addressApiLgaEndpoint' key names, these are used within dspx-core.
      addressApiSearchEndpoint: API_ENDPOINTS.address.search,
      addressApiLgaEndpoint: API_ENDPOINTS.address.lga
    }

    this.appDataService.addToApiBaseUrlsNotRequiringSpinner(API_BASE_URLS_NOT_REQUIRING_SPINNER);

    this.coreService.fileUploadFormIdentifiers = {};
    this.fileService.endpoints = {
      //Note: By default, we use the "Document/FlagForRemoval" endpoint as the fileRemoveEndpoint. This endpoint calls a function that marks the file
      //for deletion and allows the deletion to be deferred to an appropriate time. This is required for the scenario where a form with a file upload
      //control allows edits. If a user edits the form and clicks Remove on an uploaded file, we do not want the file to be immediately deleted
      //from the document management service as the user may not end up saving the form to commit the changes. Therefore, we need to defer the deletion
      //of the file until they save the form, hence the need to mark the file for deletion. If the file can be deleted immediately, use "Document/Remove"
      //as the fileRemoveEndpoint.
      fileUploadEndpoint: 'Document/Upload',
      fileRemoveEndpoint: 'Document/FlagForRemoval',
      //fileRemoveEndpoint: 'Document/Remove',
      fileDownloadEndpoint: 'api/Document/Download'
    }

    this.coreService.applicationFunctions = {};

    this.coreService.conditionFunctions = {};

    this.coreService.pageFunctions = {};

    this.coreService.repeatingPageFunctions = {};

    this.coreService.repeatingSectionFunctions = {};

    // Configure application keys by copying from HTML element attributes into AppDataService
    const getFromHtmlAndSaveToAppData = (htmlElementIdAndAppDataKey: string, htmlAttributeName = 'value') =>
      this.appDataService.setAppData(
        htmlElementIdAndAppDataKey,
        this.appDataService.getAttributeFromHtmlElement(htmlElementIdAndAppDataKey, htmlAttributeName));
    getFromHtmlAndSaveToAppData(APP_DATA_KEYS.maxGridRowCount);
    getFromHtmlAndSaveToAppData(APP_DATA_KEYS.applicationUrl);
    getFromHtmlAndSaveToAppData(APP_DATA_KEYS.googleRecaptchaSiteKey, 'key');

    // Configure the environment banner
    const environmentName =
      this.appDataService.getValueAttributeFromHtmlElement('environmentName');
    if (environmentName) {
      this.environmentBannerMessage = `You are using the ${environmentName} environment`;
    }
    const environmentBannerBgColor =
      this.appDataService.getValueAttributeFromHtmlElement(
        'environmentBannerBgColor'
      );
    if (environmentBannerBgColor) {
      this.environmentBannerBgColor = environmentBannerBgColor;
    }
    const environmentBannerColor =
      this.appDataService.getValueAttributeFromHtmlElement(
        'environmentBannerColor'
      );
    if (environmentBannerColor) {
      this.environmentBannerColor = environmentBannerColor;
    }

    // we are using MS Identity so redirect to MicrosoftIdentity Web UI Account Controller action
    this.userService.logout = () => {
      window.open('MicrosoftIdentity/Account/SignOut', '_self');
    };

    this.headerConfig = {
      applicationTitle: {
        label: APPLICATION.title,
      },
      applicationLogo: {
        source: APPLICATION.logo,
        link: {
          label: 'Victoria State Government logo'
        }
      },
    };

    // Configure the footer
    const createLink = (url: string, text: string): CardSetFooterLink =>
    ({
      label: text,
      altText: text + ' ', // Due to a silly bug in the framework, if label and altText are the same, the altText is set to "undefined", which will literally display as "undefined" in the footer.
      url: url
    });
    this.footerConfig = {
      lightModeLogo: '/assets/images/sgv-logo-dark.png',
      darkModeLogo: '/assets/images/sgv-logo.png',
      atsiLogo: '/assets/images/logo-a-tsi-flag.svg',
      prideLogo: '/assets/images/flag-pride.svg',
      prideDeclaration:
        'The department celebrates, values and includes people of all backgrounds, genders, sexualities, cultures, bodies and abilities.',
      atsiDeclaration:
        'The department acknowledges Aboriginal and Torres Strait Islander people as the Traditional Custodians of the land and acknowledges and pays respect to their Elders, past and present.',
      footerLinkColumnsHeadingLevel: 4,
      footerLinkColumns: [
        {
          heading: 'Privacy',
          links: [
            createLink(
              `/${ROUTE_CONSTANTS.collectionNotice}`,
              'Collection notice')
          ]
        },
        {
          heading: 'Other Resources',
          links: [
            createLink(
              'http://www.dffh.vic.gov.au/',
              'Department of Families, Fairness and Housing website'),
            createLink(
              'http://www.health.vic.gov.au/',
              'Department of Health website'),
            createLink(
              'https://www.vpsc.vic.gov.au/ethics-behaviours-culture/codes-of-conduct/code-of-conduct-for-victorian-public-sector-employees/',
              'Code of conduct for VPS employees'),
            createLink(
              'https://www.vpsc.vic.gov.au/ethics-behaviours-culture/codes-of-conduct/code-of-conduct-for-directors-of-victorian-public-entities/',
              'Code of conduct for directors of Victorian public entities')
          ]
        }
      ],
      departmentName: 'Department of Families, Fairness and Housing / Department of Health',
      governmentName: 'State Government of Victoria, Australia',
      showAppLogo: false,
      lightModeAppLogo: '/assets/images/OSCAR_Logo_V__RGB__Navy_Turquoise.svg',
      darkModeAppLogo: '/assets/images/OSCAR_Logo_V__RGB__Navy_Turquoise.svg',
    };

    // Get Logged In User and update header
    this.userService
      .getLoggedInUser('getLoggedInUser')
      .pipe(take(1))
      .subscribe((userProfile) => {
        if (userProfile.user !== null) {
          this.headerConfig.applicationTitle = {
            label: APPLICATION.title,
            url: this.appDataService.getAppData(APP_DATA_KEYS.applicationUrl),
            target: '_self'
          };
          this.headerConfig.loggedInUser = {
            name: userProfile.fullName,
            imageSource: '/assets/images/avatar.png',
            imageDescription: userProfile.fullName,
            menuSubItems: [
              {
                label: 'Logout',
                icon: 'pi pi-fw pi-sign-out',
                routerLink: 'logout',
              },
            ],
          };
        }
        this.headerService.onHeaderUpdated(this.headerConfig);
      });

    // Configure the menu
    const request: FormConfigurationRequest = {
      apiEndpointKey: API_ENDPOINT_KEYS.getFormConfig,
      templateId: TEMPLATES.menu,
      versionNo: VERSIONS.latest,
      entityId: null,
    };
    this.formConfigurationService
      .getFormConfigAndResponse(request)
      .pipe(take(1))
      .subscribe((data) => {
        this.menuConfig =
          this.cardSetTransformationService.extractMenuConfigFromFormConfig([
            data.configData.config,
          ]);
        this.menuService.onMenuLoad(this.menuConfig);
        if (!this.menuConfig?.items?.length) {
          this.headerCustomisationService.disableMenu();
        }
      });

    this.dialogService.updatePopupText(
      POP_UP_CODES.common.confirmDocumentDeletion,
      POP_UP_TEXTS.common.confirmDocumentDeletion);

    // Global overrides of title and text for default framework popups
    const mergePopup = (popup: { code: string, title: string, text: string }) =>
      ({ ...this.dialogService.findPopupByCode(popup.code), ...popup });
    const contactSupport =
      `<p>If the error persists, contact DFFH-DH Common Corporate Support on 1300 344 727 or log a request in  OurService.</p>`;
    const authErrorHelpText =
      `<p>
	      <ol>
		      <li>Click Close to exit this message.</li>
		      <li>Click on the text 'Integrity forms' in the banner at the top of the screen to sign in.</li>
		      <li>Use the menu on the top left of the screen to open the form you need.</li>
	      </ol>
        If you're still unable to access the form, contact DFFH-DH Common Corporate Support on 1300 344 727 or log a request in OurService.
      </p>`;
    this.dialogService.setPopups([
      mergePopup({ code: POP_UP_CODES.common.validationError, title: 'Missing information', text: `<p>Please complete all the required fields and try again.</p>` }),
      mergePopup({ code: POP_UP_CODES.common.authorisationError, title: 'Unable to access the page', text: `<p>Please check that you're signed in:</p>${authErrorHelpText}` }),
      mergePopup({ code: POP_UP_CODES.common.sessionTimeout, title: 'Unable to access the page', text: `<p>Please check that you're signed in and try again.</p>${contactSupport}` }),
      mergePopup({ code: POP_UP_CODES.common.serverError, title: 'Unable to continue', text: `<p>An unexpected error occurred.</p>${contactSupport}` })
    ]);
  }
}
